import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Trophy, Award, ChevronRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import reports from "@/api/reports";
import { TX_COLORS, colorFor } from "@/lib/reportColors";

// Vibrant palette for bars #4..#12 (and beyond). Chosen to stand out on the
// dark navy background and to NOT clash with the gold/silver/bronze podium.
const BAR_PALETTE = [
  { from: "#3B82F6", to: "#1E40AF", side: "#1E3A8A", top: "#60A5FA" }, // blue
  { from: "#10B981", to: "#047857", side: "#065F46", top: "#34D399" }, // green
  { from: "#A855F7", to: "#6B21A8", side: "#581C87", top: "#C084FC" }, // violet
  { from: "#EF4444", to: "#991B1B", side: "#7F1D1D", top: "#F87171" }, // red
  { from: "#0EA5E9", to: "#0369A1", side: "#075985", top: "#38BDF8" }, // cyan
  { from: "#EC4899", to: "#9D174D", side: "#831843", top: "#F472B6" }, // pink
  { from: "#14B8A6", to: "#0F766E", side: "#115E59", top: "#2DD4BF" }, // teal
  { from: "#F59E0B", to: "#B45309", side: "#92400E", top: "#FBBF24" }, // amber
  { from: "#6366F1", to: "#3730A3", side: "#312E81", top: "#818CF8" }, // indigo
];

const PODIUM = {
  1: {
    from: "#FFE680",
    to: "#FFA500",
    side: "#B8860B",
    top: "#FFF3A8",
    glow: "rgba(255, 215, 0, 0.55)",
    medal: "#FFD700",
  },
  2: {
    from: "#F3F4F6",
    to: "#9CA3AF",
    side: "#6B7280",
    top: "#FFFFFF",
    glow: "rgba(229, 231, 235, 0.45)",
    medal: "#E5E7EB",
  },
  3: {
    from: "#E89A52",
    to: "#8B4513",
    side: "#6B3410",
    top: "#F5B07E",
    glow: "rgba(205, 127, 50, 0.45)",
    medal: "#CD7F32",
  },
};

const TOP_OPTIONS = [5, 10, 12, 20];

// Two layout presets — desktop and mobile. Mobile keeps bars readable (44px
// wide so medals + counts don't squish) while letting the row scroll
// horizontally. Picked over CSS-only scaling because the medal/count offsets
// are pixel-based.
const LAYOUT = {
  desktop: {
    BAR_WIDTH: 56,
    COLUMN_WIDTH: 72,
    COLUMN_GAP: 20,
    MAX_H: 420,
    NAME_BAND: 92,
    TOP_PADDING: 90,
    COUNT_HEIGHT_PODIUM: 24,
    COUNT_HEIGHT_NORMAL: 20,
    MEDAL_SIZE_PODIUM_FIRST: 44,
    MEDAL_SIZE_PODIUM_REST: 36,
    NAME_FONT: 12,
  },
  mobile: {
    BAR_WIDTH: 44,
    COLUMN_WIDTH: 56,
    COLUMN_GAP: 12,
    MAX_H: 320,
    NAME_BAND: 84,
    TOP_PADDING: 78,
    COUNT_HEIGHT_PODIUM: 20,
    COUNT_HEIGHT_NORMAL: 16,
    MEDAL_SIZE_PODIUM_FIRST: 38,
    MEDAL_SIZE_PODIUM_REST: 30,
    NAME_FONT: 11,
  },
};

const COUNT_OFFSET = 6;
const MEDAL_GAP = 6;

function paletteFor(rank) {
  if (rank <= 3) return PODIUM[rank];
  const p = BAR_PALETTE[(rank - 4) % BAR_PALETTE.length];
  return { ...p, glow: `${p.from}55`, medal: null };
}

// Single 3D bar — fakes 3D using two skewed pseudo-elements (no preserve-3d
// nesting per bar, way more performant for 20 bars).
function Bar3D({ height, palette, isPodium, width, animDelayMs = 0 }) {
  return (
    <div
      className="relative flex-none"
      style={{
        width,
        height: `${height}px`,
        // No radial glow anymore — only a directional drop shadow so bar
        // edges stay crisp and never bleed light onto neighbouring text.
        filter: "drop-shadow(2px 4px 6px rgba(0,0,0,0.35))",
        animation: "bar3d-rise 700ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
        animationDelay: `${animDelayMs}ms`,
        transformOrigin: "bottom center",
      }}
    >
      {/* Top face */}
      <div
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          top: -10,
          left: 5,
          width,
          height: 14,
          background: `linear-gradient(135deg, ${palette.top}, ${palette.from})`,
          transform: "skewX(-50deg)",
          borderRadius: 1,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)",
        }}
      />
      {/* Right side face */}
      <div
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          right: -10,
          top: 5,
          width: 12,
          height: "100%",
          background: `linear-gradient(180deg, ${palette.side}, rgba(0,0,0,0.65))`,
          transform: "skewY(-40deg)",
          borderRadius: "0 1px 1px 0",
        }}
      />
      {/* Front face */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(180deg, ${palette.from} 0%, ${palette.to} 100%)`,
          borderRadius: "2px 2px 0 0",
          boxShadow:
            "inset -8px 0 16px rgba(0,0,0,0.28), inset 1px 0 0 rgba(255,255,255,0.15)",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-y-0 left-0 w-2"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0))",
          }}
        />
      </div>
      {isPodium && (
        <div
          aria-hidden
          className="absolute inset-0 rounded-t-sm opacity-30"
          style={{
            background: `linear-gradient(180deg, transparent 40%, ${palette.glow} 120%)`,
          }}
        />
      )}
    </div>
  );
}

function Medal({ rank, color, size = 36 }) {
  return (
    <div
      className="relative flex items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(145deg, #FFFFFF 0%, ${color}CC 60%, ${color} 100%)`,
        // Directional drop shadow only — no radial glow.
        boxShadow: "0 2px 4px rgba(0,0,0,0.18)",
        border: `1.5px solid ${color}`,
      }}
      data-testid={`r3d-medal-${rank}`}
    >
      <span
        className="font-extrabold text-slate-900"
        style={{
          fontSize: size >= 40 ? 22 : 16,
          lineHeight: 1,
          textShadow: "none",
          WebkitFontSmoothing: "antialiased",
          textRendering: "geometricPrecision",
        }}
      >
        {rank}
      </span>
    </div>
  );
}

// One bar column. Every "above-bar" decoration (count, medal) is absolutely
// positioned so its vertical offset is anchored to the TOP of the bar, never
// to the bar of a neighbour.
function BarColumn({ item, rank, maxValue, layout, canvasHeight, t, onClick, isSelected }) {
  const palette = paletteFor(rank);
  const pct = maxValue > 0 ? item.total / maxValue : 0;
  const barHeight = Math.max(60, Math.round(pct * layout.MAX_H));
  const isPodium = rank <= 3;
  const byType = item.by_type || {};
  const medalSize =
    rank === 1 ? layout.MEDAL_SIZE_PODIUM_FIRST : layout.MEDAL_SIZE_PODIUM_REST;
  const countHeight = isPodium
    ? layout.COUNT_HEIGHT_PODIUM
    : layout.COUNT_HEIGHT_NORMAL;
  const clickable = typeof onClick === "function";

  const barBottom = layout.NAME_BAND;
  const barTop = barBottom + barHeight;
  const countBottom = barTop + COUNT_OFFSET;
  const medalBottom = countBottom + countHeight + MEDAL_GAP;

  // Phase 8.4 — tooltip via React portal so it escapes the wrapper's
  // `overflow-hidden` (and the scroll container's implicit overflow-y clip).
  // We track hover locally + compute a fixed viewport position off the
  // column's boundingRect, then auto-flip below when the tooltip would clip
  // the top of the viewport.
  const columnRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [tipPos, setTipPos] = useState(null);

  const updateTipPos = () => {
    const el = columnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Locate the *top of the bar* in viewport coordinates. The column is
    // as tall as the whole canvas, so we can't use r.top — we need the
    // point where the medal/count/tooltip stack should anchor.
    // Layout inside the column (from top): canvasHeight - medalBottom - medalSize.
    const stackTopFromColumnTop = Math.max(0, canvasHeight - medalBottom - medalSize);
    const stackTopY = r.top + stackTopFromColumnTop;
    // Reserve ~220px for the tooltip + 12px gap above the stack.
    const RESERVED_ABOVE = 220;
    const flipBelow = stackTopY - RESERVED_ABOVE < 12;
    setTipPos({
      left: r.left + r.width / 2,
      top: flipBelow ? r.bottom + 12 : stackTopY - 12,
      placement: flipBelow ? "below" : "above",
    });
  };

  return (
    <div
      ref={columnRef}
      className={`group relative flex-none ${
        clickable ? "cursor-pointer" : ""
      } ${isSelected ? "ring-2 ring-yellow-400 ring-offset-2 ring-offset-transparent" : ""}`}
      style={{
        width: layout.COLUMN_WIDTH,
        height: canvasHeight,
        transition: "transform 150ms ease-out",
      }}
      onMouseEnter={() => {
        setHover(true);
        updateTipPos();
      }}
      onMouseLeave={() => setHover(false)}
      onClick={clickable ? () => onClick(item) : undefined}
      onMouseOver={
        clickable
          ? (e) => (e.currentTarget.style.transform = "translateY(-3px)")
          : undefined
      }
      onMouseOut={
        clickable
          ? (e) => (e.currentTarget.style.transform = "translateY(0)")
          : undefined
      }
      data-testid={`r3d-col-${item.employee_id}`}
    >
      {clickable && (
        <div
          data-testid={`r3d-bar-${item.employee_id}`}
          className="absolute inset-0"
          aria-label={`Filtrar por ${item.employee_name}`}
        />
      )}
      {/* Bar — anchored to the bottom of the chart floor (above NAME_BAND) */}
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{ bottom: barBottom }}
      >
        <Bar3D
          height={barHeight}
          palette={palette}
          isPodium={isPodium}
          width={layout.BAR_WIDTH}
          animDelayMs={rank * 50}
        />
      </div>

      {/* Floating count above the bar — rendered as a solid white pill so the
          text lives on a controlled background and needs zero shadow/glow.
          Result: identical crispness to the Y-axis tick labels. */}
      <div
        className={`pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 tabular-nums ${
          isPodium ? "text-sm" : "text-xs"
        }`}
        style={{
          bottom: countBottom,
          minWidth: 44,
          textAlign: "center",
          whiteSpace: "nowrap",
          color: "#0F172A",
          fontWeight: 800,
          background: "rgba(255,255,255,0.96)",
          padding: "2px 6px",
          borderRadius: 6,
          boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
          textShadow: "none",
          filter: "none",
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          textRendering: "geometricPrecision",
        }}
        data-testid={`r3d-count-${item.employee_id}`}
      >
        {item.total.toLocaleString()}
      </div>

      {/* Medal — only for top 3 */}
      {isPodium && (
        <div
          className="pointer-events-none absolute left-1/2 z-10 -translate-x-1/2"
          style={{ bottom: medalBottom }}
          data-testid={`r3d-medal-wrap-${rank}`}
        >
          <Medal rank={rank} color={palette.medal} size={medalSize} />
        </div>
      )}

      {/* Slanted name — no italic (italic + rotate is the worst-case
          antialiasing scenario in HTML text), no shadow, no filter. Solid
          slate-700 on either background gives AA contrast crispness. */}
      <div
        className="absolute left-0 bottom-0 overflow-visible"
        style={{ width: layout.COLUMN_WIDTH, height: layout.NAME_BAND }}
      >
        <div
          style={{
            transform: "rotate(35deg)",
            transformOrigin: "top left",
            fontSize: layout.NAME_FONT,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            paddingLeft: 14,
            paddingTop: 8,
            color: "#334155",
            fontWeight: 600,
            textShadow: "none",
            filter: "none",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
          }}
          className="dark:!text-slate-200"
          title={item.employee_name}
          data-testid={`r3d-name-${item.employee_id}`}
        >
          {item.employee_name}
        </div>
      </div>

      {/* Hover tooltip — portalled to body to escape overflow-hidden. */}
      {hover && tipPos && createPortal(
        <div
          className="pointer-events-none fixed z-[9999]"
          style={{
            left: tipPos.left,
            top: tipPos.top,
            transform:
              tipPos.placement === "above"
                ? "translate(-50%, -100%)"
                : "translate(-50%, 0)",
            minWidth: 220,
          }}
          role="tooltip"
          data-testid={`r3d-tooltip-${item.employee_id}`}
        >
          <div className="rounded-lg border border-slate-700/80 bg-slate-900/95 px-3 py-2 text-left shadow-2xl backdrop-blur">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-slate-100">
                {item.employee_name}
              </span>
              <span className="rounded-sm border border-slate-600 px-1.5 py-px font-mono text-[10px] text-slate-300">
                {item.employee_id}
              </span>
            </div>
            <div className="mt-1 flex items-baseline gap-1.5">
              <span className="text-2xl font-bold tabular-nums text-yellow-300">
                {item.total.toLocaleString()}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-slate-400">
                {t("ranking3d.totalLabel")}
              </span>
            </div>
            {Object.keys(byType).length > 0 && (
              <div className="mt-2 space-y-1 border-t border-slate-700/50 pt-2">
                {Object.entries(byType)
                  .sort((a, b) => b[1] - a[1])
                  .map(([tt, count]) => (
                    <div
                      key={tt}
                      className="flex items-center justify-between gap-3 text-[11px]"
                    >
                      <span className="flex items-center gap-1.5 text-slate-300">
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ background: TX_COLORS[tt] || colorFor(tt, 0) }}
                        />
                        {tt}
                      </span>
                      <span className="tabular-nums font-semibold text-slate-100">
                        {count.toLocaleString()}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// Breakpoint helper. 720px matches the spec ("scroll appears < 720px") and
// also lets us pick a tighter layout preset on small tablets.
function useIsMobile(threshold = 720) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < threshold;
  });
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${threshold - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [threshold]);
  return isMobile;
}

// Watch a scrolling element + its child width to derive (a) whether the
// content overflows horizontally and (b) whether the user is already at the
// right edge. Used to fade-in/fade-out the "more →" hint.
// `deps` re-runs the effect when the consumer knows the DOM might have
// re-rendered (e.g. items arrived after a fetch).
function useHorizontalOverflow(ref, deps = []) {
  const [state, setState] = useState({ hasOverflow: false, atRight: true });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const recompute = () => {
      const hasOverflow = el.scrollWidth - el.clientWidth > 2;
      const atRight =
        !hasOverflow ||
        Math.abs(el.scrollLeft + el.clientWidth - el.scrollWidth) < 4;
      setState({ hasOverflow, atRight });
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    el.addEventListener("scroll", recompute, { passive: true });
    window.addEventListener("resize", recompute);
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", recompute);
      window.removeEventListener("resize", recompute);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...deps]);

  return state;
}

export default function Ranking3DExtended({
  payload,
  defaultTop = 12,
  testId = "ranking-3d-extended",
  onEmployeeClick,
  selectedEmployeeId = null,
}) {
  const { t } = useTranslation();
  const [top, setTop] = useState(defaultTop);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile(720);
  const layout = isMobile ? LAYOUT.mobile : LAYOUT.desktop;
  const canvasHeight = layout.MAX_H + layout.TOP_PADDING + layout.NAME_BAND;
  const scrollRef = useRef(null);
  // Pass items.length so the overflow detector recomputes once the data
  // arrives — the first effect tick runs while the canvas is still empty.
  const { hasOverflow, atRight } = useHorizontalOverflow(scrollRef, [
    items.length,
    layout,
  ]);

  // Stringify the filter payload so referential changes don't trigger needless
  // refetches (intentional — payload identity changes on every render).
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    reports
      .ranking(payload, top)
      .then((r) => alive && setItems(r.data?.items || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // payload tracked indirectly via payloadKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, top]);

  const maxValue = items[0]?.total || 1;

  // Y-axis ticks: 5 evenly spaced.
  const ticks = useMemo(() => {
    const arr = [];
    for (let i = 4; i >= 0; i -= 1) {
      arr.push(Math.round((maxValue * i) / 4));
    }
    return arr;
  }, [maxValue]);

  const barsRowWidth =
    items.length * layout.COLUMN_WIDTH +
    Math.max(items.length - 1, 0) * layout.COLUMN_GAP;
  // Always reserve >= barsRowWidth so bars never compress. The +24 keeps a
  // small breathing margin on the right edge.
  const canvasWidth = barsRowWidth + 24;
  const yAxisWidth = isMobile ? 52 : 64;

  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden rounded-xl border border-border bg-card dark:border-slate-700/40 dark:[background:linear-gradient(180deg,#0F172A_0%,#020617_100%)]"
    >
      {/* Subtle grid lines pattern overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04] dark:opacity-[0.06]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent 0 47px, rgba(148,163,184,1) 47px 48px), repeating-linear-gradient(90deg, transparent 0 47px, rgba(148,163,184,1) 47px 48px)",
        }}
      />

      {/* Header */}
      <div className="relative flex flex-wrap items-start justify-between gap-3 px-4 pt-5 sm:px-6">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{
              background:
                "linear-gradient(135deg, rgba(253,224,71,0.25), rgba(245,158,11,0.15))",
              boxShadow:
                "inset 0 0 0 1px rgba(253,224,71,0.4), 0 0 18px rgba(253,224,71,0.18)",
            }}
          >
            <Trophy className="h-5 w-5 text-yellow-500 dark:text-yellow-300" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {t("ranking3d.title")}
            </h3>
            <p className="text-xs text-muted-foreground">{t("ranking3d.subtitle")}</p>
          </div>
        </div>
        <Select value={String(top)} onValueChange={(v) => setTop(Number(v))}>
          <SelectTrigger
            className="h-8 w-28 border-border bg-background text-xs text-foreground hover:bg-muted dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-200 dark:hover:bg-slate-800"
            data-testid={`${testId}-top-select`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TOP_OPTIONS.map((n) => (
              <SelectItem
                key={n}
                value={String(n)}
                data-testid={`${testId}-top-${n}`}
              >
                Top {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Plot area */}
      <div className="relative px-4 py-6 sm:px-6">
        {loading ? (
          <div
            className="flex h-[520px] items-center justify-center text-sm text-muted-foreground"
            data-testid={`${testId}-loading`}
          >
            {t("common.loading")}
          </div>
        ) : items.length === 0 ? (
          <div
            className="flex h-[520px] items-center justify-center text-sm text-muted-foreground"
            data-testid={`${testId}-empty`}
          >
            {t("common.no_data")}
          </div>
        ) : (
          <div className="relative flex items-stretch">
            {/* Y-axis — lives OUTSIDE the horizontal scroll container so the
                tick labels stay pinned to the left while the bars row scrolls.
                z-20 sits above the right-edge fade hint. */}
            <div
              className="relative z-20 flex-none bg-gradient-to-r from-card via-card to-transparent dark:from-[#0F172A] dark:via-[#0F172A]"
              style={{ width: yAxisWidth, height: canvasHeight }}
              data-testid={`${testId}-y-axis`}
            >
              {ticks.map((v, i) => {
                const tickTop = layout.TOP_PADDING + (i / 4) * layout.MAX_H;
                return (
                  <div
                    key={i}
                    className="absolute right-3 tabular-nums text-[10px] text-muted-foreground"
                    style={{ top: tickTop, transform: "translateY(-50%)" }}
                  >
                    {v.toLocaleString()}
                    <span className="ml-1 text-muted-foreground/70">tx</span>
                  </div>
                );
              })}
            </div>

            {/* Bars + gridlines canvas, wrapped in a relative parent so the
                right-edge fade hint can be positioned against the VISIBLE
                viewport instead of the scrolled content. `min-w-0` is the
                classic flex fix that lets `overflow-x-auto` actually trigger
                instead of letting the bars expand the flex track. */}
            <div className="relative min-w-0 flex-1">
              <div
                ref={scrollRef}
                className="overflow-x-auto"
                style={{ scrollbarWidth: "thin" }}
                data-testid={`${testId}-scroll`}
              >
                <div
                  className={`relative ${hasOverflow ? "" : "mx-auto"}`}
                  style={{
                    width: canvasWidth,
                    height: canvasHeight,
                  }}
                >
                  {/* Horizontal gridlines, aligned to the Y ticks */}
                  {ticks.map((_, i) => {
                    const tickTop = layout.TOP_PADDING + (i / 4) * layout.MAX_H;
                    return (
                      <div
                        key={i}
                        aria-hidden
                        className="absolute left-0 right-0 border-t border-border/60 dark:border-slate-700/40"
                        style={{ top: tickTop }}
                      />
                    );
                  })}

                  {/* Bars row */}
                  <div
                    className={`absolute inset-0 flex items-end ${
                      hasOverflow ? "justify-start pl-2" : "justify-center"
                    }`}
                    style={{ gap: layout.COLUMN_GAP }}
                  >
                    {items.map((it, i) => (
                      <BarColumn
                        key={it.employee_id}
                        item={it}
                        rank={i + 1}
                        maxValue={maxValue}
                        layout={layout}
                        canvasHeight={canvasHeight}
                        t={t}
                        onClick={onEmployeeClick}
                        isSelected={
                          selectedEmployeeId != null &&
                          String(selectedEmployeeId).toUpperCase() ===
                            String(it.employee_id).toUpperCase()
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Right-edge fade hint — pinned to the visible viewport border
                  (sibling of the scroll container, NOT child of it). */}
              <div
                aria-hidden
                className={`pointer-events-none absolute right-0 top-0 flex items-center justify-end transition-opacity duration-200 ${
                  hasOverflow && !atRight ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  height: canvasHeight,
                  width: 56,
                  background:
                    "linear-gradient(90deg, rgba(2,6,23,0) 0%, rgba(2,6,23,0.85) 75%, rgba(2,6,23,0.95) 100%)",
                }}
                data-testid={`${testId}-scroll-fade`}
              >
                <ChevronRight
                  className="mr-1 h-5 w-5 animate-pulse text-slate-300"
                  style={{ filter: "drop-shadow(0 0 4px rgba(0,0,0,0.6))" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Floor line */}
        <div className="mt-1 h-px w-full bg-gradient-to-r from-transparent via-border to-transparent dark:via-slate-600/40" />

        {/* Footer hint */}
        {items.length > 0 && (
          <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
            <Award className="h-3 w-3 text-yellow-500 dark:text-yellow-300" />
            <span>{t("ranking3d.hoverHint")}</span>
          </div>
        )}
      </div>

      {/* Keyframe for the rise-in animation */}
      <style>{`
        @keyframes bar3d-rise {
          0% { transform: scaleY(0.04); opacity: 0; }
          60% { opacity: 1; }
          100% { transform: scaleY(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
